// =====================================================================================
// INVRT Radar — web console — Supabase browser client
//
// Used for exactly three things:
//   1. Magic-link sign-in and session handling. Radar has NO passwords — the platform
//      never issues one, so there is no password field anywhere in this console.
//   2. Reading `memberships` (the only source of org scope) to populate the org switcher.
//   3. Reading the caller's own `profiles` row for `is_super_admin`, which decides whether
//      the operator console appears. The SERVER enforces that flag on every route and RPC;
//      showing or hiding a nav item is a courtesy, never a control.
//
// Everything else goes through the api Edge Function (src/lib/api.ts).
// =====================================================================================

import { createClient, type SupabaseClient } from "@supabase/supabase-js";

let client: SupabaseClient | null = null;

export function supabaseConfig(): { url: string; anonKey: string } | null {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) return null;
  return { url: url.replace(/\/+$/, ""), anonKey };
}

/**
 * The single browser client. Throws when the build has no configuration, which is a
 * deployment fault worth failing loudly on rather than rendering a sign-in form that
 * cannot possibly work.
 */
export function getSupabase(): SupabaseClient {
  if (client) return client;
  const config = supabaseConfig();
  if (!config) {
    throw new Error(
      "Radar is not configured: set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (see web/.env.example).",
    );
  }
  client = createClient(config.url, config.anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: "pkce",
    },
  });
  return client;
}

/** Where the magic link returns the browser to. */
export function authRedirectUrl(): string {
  const configured = import.meta.env.VITE_AUTH_REDIRECT_URL;
  if (configured) return configured.replace(/\/+$/, "");
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

/** Test seam. Clears the memoised client so a suite can install its own. */
export function _resetSupabaseForTests(next: SupabaseClient | null = null): void {
  client = next;
}
